import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { provideRouter } from '@angular/router';

import { provideIonicAngular } from '@ionic/angular/standalone';

import {
  AUTH_SESSION,
  provideAuthRoutePaths,
  provideAuthSession,
} from '@the-hive-frontend/auth-session';
import { keycloakAuthInterceptor, provideKeycloakAuth } from '@the-hive-frontend/keycloak-auth';
import { filter, firstValueFrom, take } from 'rxjs';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { demoInterceptor } from './core/demo/demo.interceptor';
import { DemoModeService } from './core/demo/demo-mode.service';
import { tenantInterceptor } from './core/interceptors/tenant.interceptor';
import { GlobalErrorHandler } from './core/services/global-error-handler.service';
import { ClientStore } from './core/stores/client.store';
import { TenantStore } from './core/stores/tenant.store';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    // demoInterceptor MUST stay first — see core/demo/demo.interceptor.ts.
    provideHttpClient(
      withInterceptors([demoInterceptor, keycloakAuthInterceptor, tenantInterceptor])
    ),
    provideKeycloakAuth({
      issuer: environment.auth.kcIssuer,
      clientId: environment.auth.kcClientId,
      redirectUri: environment.auth.kcRedirectUri,
      scopes: environment.auth.kcScopes,
      silentSso: true,
    }),
    provideAuthSession(),
    provideAuthRoutePaths({ login: '/login', forbidden: '/error', home: '/home' }),
    provideAppInitializer(async () => {
      const session = inject(AUTH_SESSION);
      const tenantStore = inject(TenantStore);
      const clientStore = inject(ClientStore);
      const demoMode = inject(DemoModeService);

      // toObservable() needs an injection context, and the first `await` below
      // leaves it — so build the stream now, while we still have one. Creating
      // it before the demo-mode short-circuit is harmless: nothing subscribes
      // unless we reach the firstValueFrom().
      const sessionStatus$ = toObservable(session.status);

      if (await demoMode.bootstrap()) {
        return;
      }

      await firstValueFrom(
        sessionStatus$.pipe(
          filter((status) => status !== 'unknown'),
          take(1)
        )
      );

      if (session.status() !== 'authenticated') {
        return;
      }

      await tenantStore.init();
      await clientStore.init();
    }),
    provideIonicAngular({}),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
};
